import { useMemo, useState } from "react";
import { BookmarkPlus, BookmarkCheck, Clock, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Reveal, SectionHead, PageHero, GoldBtn } from "@/components/Shared";
import { PROGRAMME, SESSION_TYPES } from "@/data/content";

const loadSaved = () => {
    try {
        return JSON.parse(localStorage.getItem("mb_schedule") || "[]");
    } catch {
        return [];
    }
};

export default function Programme() {
    const [day, setDay] = useState(1);
    const [type, setType] = useState("All");
    const [savedOnly, setSavedOnly] = useState(false);
    const [saved, setSaved] = useState(loadSaved);

    const toggleSave = (s) => {
        const key = `${s.day}-${s.time}-${s.title}`;
        let next;
        if (saved.includes(key)) {
            next = saved.filter((k) => k !== key);
            toast("Removed from your schedule");
        } else {
            next = [...saved, key];
            toast.success("Added to your schedule");
        }
        setSaved(next);
        localStorage.setItem("mb_schedule", JSON.stringify(next));
    };

    const items = useMemo(
        () =>
            PROGRAMME.filter((s) => {
                const key = `${s.day}-${s.time}-${s.title}`;
                if (savedOnly && !saved.includes(key)) return false;
                if (!savedOnly && s.day !== day) return false;
                if (type !== "All" && s.type !== type) return false;
                return true;
            }),
        [day, type, savedOnly, saved],
    );

    return (
        <div data-testid="programme-page">
            <PageHero
                testid="programme-hero"
                eyebrow="Programme · 29–31 January 2027"
                title={<>Three Days. <span className="gold-text">Every Hour Counts.</span></>}
                sub="MahaTalks, PitchYatra rounds, masterclasses, investor sessions and evening experiences — filter the festival and build your own schedule."
            />

            <section className="py-16 md:py-24">
                <div className="container-x">
                    <Reveal>
                        <div className="flex flex-wrap items-center gap-3" role="tablist" aria-label="Programme days">
                            {[1, 2, 3].map((d) => (
                                <button key={d} onClick={() => { setDay(d); setSavedOnly(false); }}
                                    role="tab" aria-selected={day === d && !savedOnly}
                                    data-testid={`day-tab-${d}`}
                                    className={`px-7 py-3.5 text-[0.66rem] tracking-[0.3em] uppercase border transition-all duration-400 ${
                                        day === d && !savedOnly
                                            ? "border-[var(--gold)] text-[#171106] bg-gradient-to-r from-[#8C6A2F] via-[#C9A24B] to-[#E8CF8F] font-semibold"
                                            : "hairline text-[var(--muted)] hover:text-[var(--champagne)]"
                                    }`}>
                                    Day 0{d} <span className="hidden sm:inline opacity-70">· {["29", "30", "31"][d - 1]} Jan</span>
                                </button>
                            ))}
                            <button onClick={() => setSavedOnly(!savedOnly)} data-testid="saved-schedule-toggle"
                                className={`ml-auto px-5 py-3.5 text-[0.62rem] tracking-[0.26em] uppercase border transition-all flex items-center gap-2 ${
                                    savedOnly ? "border-[var(--gold)] text-[var(--champagne)]" : "hairline text-[var(--muted)] hover:text-[var(--champagne)]"
                                }`}>
                                <BookmarkCheck size={13} /> My Schedule ({saved.length})
                            </button>
                        </div>
                        <div className="mt-5 flex flex-wrap gap-2" data-testid="programme-filters">
                            {["All", ...SESSION_TYPES].map((t) => (
                                <button key={t} onClick={() => setType(t)} data-testid={`filter-${t.toLowerCase().replace(/\s+/g, "-")}`}
                                    className={`chip ${type === t ? "chip-on" : ""}`}>
                                    {t}
                                </button>
                            ))}
                        </div>
                    </Reveal>

                    <div className="mt-12 max-w-4xl" data-testid="programme-timeline">
                        {items.length === 0 && (
                            <p className="py-16 text-center text-muted-luxe text-sm border hairline">
                                {savedOnly ? "No saved sessions yet — tap “Add to My Schedule” on any session." : "No sessions match this filter on the selected day."}
                            </p>
                        )}
                        {items.map((s, i) => {
                            const key = `${s.day}-${s.time}-${s.title}`;
                            const isSaved = saved.includes(key);
                            return (
                                <Reveal key={key} delay={Math.min(i * 60, 300)}>
                                    <div className="tl-item py-6 group" data-testid={`session-${s.day}-${i}`}>
                                        <div className="card-luxe p-6 md:p-7 !transform-none hover:!translate-y-0 hover:border-[var(--line)]">
                                            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[0.6rem] tracking-[0.28em] uppercase">
                                                <span className="flex items-center gap-1.5 text-[var(--gold)]"><Clock size={11} /> {s.time}</span>
                                                <span className="text-[var(--champagne)]/80">{s.type}</span>
                                                <span className="flex items-center gap-1.5 text-muted-luxe"><MapPin size={11} /> {s.venue}</span>
                                                {savedOnly && <span className="text-muted-luxe">Day 0{s.day}</span>}
                                            </div>
                                            <h3 className="font-display text-xl md:text-2xl mt-3 text-[var(--ivory)]">{s.title}</h3>
                                            <p className="mt-2 text-sm text-muted-luxe leading-relaxed">{s.desc}</p>
                                            <button onClick={() => toggleSave(s)} data-testid={`save-session-${s.day}-${i}`}
                                                className={`mt-4 inline-flex items-center gap-2 text-[0.6rem] tracking-[0.28em] uppercase transition-colors ${
                                                    isSaved ? "text-[var(--gold)]" : "text-muted-luxe hover:text-[var(--champagne)]"
                                                }`}>
                                                {isSaved ? <BookmarkCheck size={13} /> : <BookmarkPlus size={13} />}
                                                {isSaved ? "Saved to My Schedule" : "Add to My Schedule"}
                                            </button>
                                        </div>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>

                    <Reveal className="mt-14 text-center">
                        <p className="text-sm text-muted-luxe mb-6">Full programme with speakers will be announced closer to the festival.</p>
                        <GoldBtn to="/register" testid="programme-register-btn">Register to Attend</GoldBtn>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
